import * as React from "react"
import { cn } from "../lib/utils"

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Optional icon rendered on the left inside the field. */
  leftIcon?: React.ReactNode
  /** Optional node rendered on the right (icon / button). */
  rightIcon?: React.ReactNode
  /** Renders the error (destructive) ring + border. */
  invalid?: boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, leftIcon, rightIcon, invalid, ...props }, ref) => {
    const field = (
      <input
        type={type}
        ref={ref}
        aria-invalid={invalid || undefined}
        className={cn(
          "flex h-9 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground",
          "transition-all duration-fast ease-default",
          "placeholder:text-text-muted",
          "focus-visible:outline-none focus-visible:border-primary focus-visible:bg-card focus-visible:ring-2 focus-visible:ring-primary/20",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          invalid &&
            "border-2 border-destructive focus-visible:border-destructive focus-visible:ring-destructive/15",
          leftIcon && "pl-11",
          rightIcon && "pr-11",
          className
        )}
        {...props}
      />
    )

    if (!leftIcon && !rightIcon) return field

    return (
      <div className="relative flex items-center">
        {leftIcon && (
          <span className="pointer-events-none absolute left-4 flex text-text-tertiary [&_svg]:size-4">
            {leftIcon}
          </span>
        )}
        {field}
        {rightIcon && (
          <span className="absolute right-4 flex text-text-tertiary [&_svg]:size-4">
            {rightIcon}
          </span>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
